import type { ReactNode } from 'react';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authAPI, type AuthUser } from '@/lib/api/auth';
import { apiClient } from '@/lib/api/client';
import { getApiErrorStatus, isExpectedLoginError } from '@/lib/api/error-policy';
import logger from '@/lib/utils/logger';
import { ANONYMOUS_ID_STORAGE_KEY, getStoredAnonymousId } from '@/lib/utils/analytics-session';
import { clearRefreshSessionMarker, shouldRefreshSessionBootstrap } from '@/lib/auth-runtime';
import { useSessionLifecycle } from '@/hooks/useSessionLifecycle';
import {
  AuthActionsContext,
  AuthStateContext,
  type AuthActionsContextType,
  type AuthStateContextType,
} from '@/contexts/auth-context-core';

// Anonymous session conversion helper
async function convertAnonymousSession(userId: number): Promise<void> {
  try {
    const anonymousId = getStoredAnonymousId();
    if (!anonymousId) return;

    await apiClient.post('/analytics/anonymous/convert', {
      anonymous_id: anonymousId,
      user_id: userId,
    });
    localStorage.removeItem(ANONYMOUS_ID_STORAGE_KEY);
  } catch (error: unknown) {
    logger.debug('Anonymous analytics conversion failed', error);
  }
}

type User = AuthUser;

interface AuthProviderProps {
  children: ReactNode;
}

// Mock user removed - using real API authentication only

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { endSession: endAnalyticsSession } = useSessionLifecycle(user);
  // Use ref for activity tracking to avoid re-renders on every mouse move
  const lastActivityRef = useRef<number>(Date.now());
  const lastRefreshRef = useRef<number>(Date.now());
  const refreshingRef = useRef<boolean>(false);
  // Throttle für handleActivity - nur alle 5 Sekunden ausführen
  const lastActivityExecutedRef = useRef<number>(0);

  // Refs for stable callbacks that need access to current state
  const userRef = useRef<User | null>(null);
  const tokenRef = useRef<string | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async (): Promise<void> => {
      try {
        let response = await authAPI.getSessionStatus();
        if (cancelled) {
          return;
        }

        let session = response.data;
        let refreshAttempted = false;
        if (shouldRefreshSessionBootstrap(session, document.cookie, refreshAttempted)) {
          refreshAttempted = true;
          try {
            await authAPI.refresh();
            response = await authAPI.getSessionStatus();
            session = response.data;
          } catch (refreshError: unknown) {
            logger.debug('Guest session could not be restored during auth bootstrap', refreshError);
            // 401/403 proves the refresh-session marker is stale (its session is
            // gone). Drop it so later page loads no longer fire a doomed refresh.
            const refreshStatus = getApiErrorStatus(refreshError);
            if (refreshStatus === 401 || refreshStatus === 403) {
              clearRefreshSessionMarker();
            }
          }
        }

        if (!session.authenticated) {
          setUser(null);
          setToken(null);
          logger.debug('Auth initialized for guest session');
          return;
        }

        setUser(session.user);
        // Cookie-basierte Auth – Marker genügt
        setToken('cookie');
        lastRefreshRef.current = Date.now();
        logger.debug('Auth initialized for user:', session.user.username);

        // Invalidate design-configuration to ensure feature flags are fresh
        void queryClient.invalidateQueries({ queryKey: ['design-configuration'] });
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        logger.warn('Auth initialization failed:', error);
        setToken(null);
        setUser(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    initAuth().catch((error: unknown) => {
      if (!cancelled) {
        logger.error('Critical auth initialization error:', error);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  // Stable login callback - never changes reference
  const login = useCallback(
    async (email: string, password: string, rememberMe: boolean) => {
      try {
        const response = await authAPI.login({ email, password, remember_me: rememberMe });
        const userData = response.data.user;
        // Update state immediately
        setToken('cookie');
        setUser(userData);
        lastRefreshRef.current = Date.now();

        // Force a state update to ensure components re-render
        logger.info('Login successful:', { user: userData.username, role: userData.role });

        // Convert anonymous session to authenticated user (link pre-auth events)
        void convertAnonymousSession(userData.id);

        // Invalidate design-configuration to reload feature flags (e.g., voice memo)
        void queryClient.invalidateQueries({ queryKey: ['design-configuration'] });

        return { success: true, user: userData };
      } catch (error: unknown) {
        if (!isExpectedLoginError(error)) {
          logger.error('Login error:', error);
        }
        // Clear any partial state
        setToken(null);
        setUser(null);
        // Cookies werden serverseitig verwaltet
        throw error;
      }
    },
    [queryClient]
  );

  // Stable register callback - never changes reference
  const register = useCallback(
    async (username: string, email: string, password: string, termsAccepted: boolean, referredBy?: string) => {
      const params = referredBy ? { referred_by: referredBy } : undefined;
      const response = await authAPI.register({ username, email, password, terms_accepted: termsAccepted }, params);
      const userData = response.data.user;
      setToken('cookie');
      setUser(userData);

      // Convert anonymous session to authenticated user (link pre-auth events)
      void convertAnonymousSession(userData.id);

      // Invalidate design-configuration to reload feature flags
      void queryClient.invalidateQueries({ queryKey: ['design-configuration'] });

      return { success: true, user: userData };
    },
    [queryClient]
  );

  const refreshSessionState = useCallback(async (): Promise<void> => {
    const response = await authAPI.getSessionStatus();
    const session = response.data;
    if (!session.authenticated) {
      setToken(null);
      setUser(null);
      return;
    }

    setToken('cookie');
    setUser(session.user);
    lastRefreshRef.current = Date.now();
    void queryClient.invalidateQueries({ queryKey: ['design-configuration'] });
  }, [queryClient]);

  // Stable logout callback - never changes reference
  const logout = useCallback((): void => {
    const previousUser = userRef.current;
    const previousToken = tokenRef.current;

    // Stop authenticated consumers immediately so they cannot race the cookie
    // deletion with another protected request. Restore the real session state
    // if the server rejects the logout.
    setToken(null);
    setUser(null);

    const finishServerLogout = async (): Promise<void> => {
      try {
        await endAnalyticsSession('logout');
      } catch (error: unknown) {
        // Analytics must never prevent cookie invalidation, but its failure is
        // still visible for diagnosis.
        logger.warn('Analytics session end failed during logout:', error);
      }
      await authAPI.logout();
    };

    void finishServerLogout().catch((error: unknown) => {
      logger.error('Logout request failed:', error);
      setToken(previousToken);
      setUser(previousUser);
    });
  }, [endAnalyticsSession]);

  // Cookie-basierte Auth: Eingeloggt, sobald ein User geladen ist
  const isAuthenticated = !!user;

  // Stable updateUserBalance callback - uses ref to avoid dependency on user state
  const updateUserBalance = useCallback((newBalance: number) => {
    setUser((prevUser) => {
      if (prevUser) {
        return {
          ...prevUser,
          credits: newBalance,
        };
      }
      return prevUser;
    });
  }, []);

  // Stable refreshTokenOnActivity callback - never changes reference
  const refreshTokenOnActivity = useCallback(async () => {
    // Prevent multiple simultaneous refresh attempts
    if (refreshingRef.current) {
      lastActivityRef.current = Date.now();
      return;
    }

    // Cookie-basierter Refresh – nur bei Aktivität auslösen

    // Nur refreshen wenn Token bald abläuft (< 10 Minuten)
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    const tenMinutes = 10 * 60 * 1000;

    if (timeSinceActivity < tenMinutes) {
      // Token ist noch frisch, nur Activity updaten
      lastActivityRef.current = Date.now();
      return;
    }

    refreshingRef.current = true;
    try {
      await authAPI.refresh();
      setToken('cookie');
      lastActivityRef.current = Date.now();
      lastRefreshRef.current = Date.now();

      logger.debug('Token refreshed on activity');
    } catch (error: unknown) {
      logger.warn('Token refresh failed:', error);
      logout();
    } finally {
      refreshingRef.current = false;
    }
  }, [logout]);

  // Activity Tracking – erweitert um Fokus/Visibility und Route-Wechsel
  useEffect(() => {
    if (!token) return;

    const handleActivity = (): void => {
      const now = Date.now();

      // Throttle: nur alle 5 Sekunden wirklich ausführen
      const THROTTLE_MS = 5000;
      if (now - lastActivityExecutedRef.current < THROTTLE_MS) {
        return;
      }
      lastActivityExecutedRef.current = now;

      // Update ref instead of state to avoid re-renders
      lastActivityRef.current = now;

      // Proactive token refresh when enough time has passed since the last
      // refresh. Triggered by real user activity rather than a fixed timer —
      // an idle tab will let the session expire naturally, and the next
      // interaction (or a 401 from any API call) picks things up.
      const REFRESH_INTERVAL_MS = 20 * 60 * 1000;
      if (!refreshingRef.current && now - lastRefreshRef.current > REFRESH_INTERVAL_MS) {
        refreshingRef.current = true;
        authAPI
          .refresh()
          .then(() => {
            lastRefreshRef.current = Date.now();
            setToken('cookie');
            logger.debug('Token auto-refreshed on activity');
          })
          .catch((error: unknown) => {
            logger.warn('Activity-triggered token refresh failed:', error);
            logout();
          })
          .finally(() => {
            refreshingRef.current = false;
          });
      }
    };

    // Track verschiedene Activity-Events
    const events = ['click', 'keydown', 'scroll', 'mousemove', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Sichtbarkeit/Fokus des Tabs
    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };
    const handleFocus = (): void => {
      handleActivity();
    };
    const handlePageShow = (): void => {
      handleActivity();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    // Route-Wechsel via History API abfangen
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    const handleHistoryChange = (): void => {
      handleActivity();
    };
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      origPush(...args);
      handleHistoryChange();
    };
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      origReplace(...args);
      handleHistoryChange();
    };
    window.addEventListener('popstate', handleHistoryChange);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      history.pushState = origPush;
      history.replaceState = origReplace;
      window.removeEventListener('popstate', handleHistoryChange);
    };
  }, [logout, token]);

  // Memoize state value - only changes when state actually changes
  const stateValue = useMemo<AuthStateContextType>(
    () => ({
      user,
      token,
      isLoading,
      loading: isLoading,
      isAuthenticated,
    }),
    [user, token, isLoading, isAuthenticated]
  );

  // Memoize actions value - these functions never change (stable references via useCallback)
  const actionsValue = useMemo<AuthActionsContextType>(
    () => ({
      login,
      register,
      logout,
      refreshSessionState,
      updateUserBalance,
      refreshTokenOnActivity,
    }),
    [login, register, logout, refreshSessionState, updateUserBalance, refreshTokenOnActivity]
  );

  return (
    <AuthStateContext.Provider value={stateValue}>
      <AuthActionsContext.Provider value={actionsValue}>{children}</AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
};
